import { Link } from 'react-router'
import { TextareaField } from '#app/components/form/textarea-field.tsx'
import { IntakeChannelCard } from '#app/routes/mocra+/_form+/components/intake-channels/card.tsx'
import {
	getIntakeChannelIcon,
	type IntakeChannelValue,
} from '#app/routes/mocra+/_form+/components/intake-channels/icons.ts'
import { Section } from '#app/routes/mocra+/_form+/components/section.tsx'
import { type SectionResources } from '#app/routes/mocra+/_form+/section-resources.ts'

type Props = {
	readonly resources: SectionResources
	readonly value: string
	readonly errors: readonly string[]
	readonly selectedChannels: ReadonlyArray<IntakeChannelValue>
	readonly options: ReadonlyArray<{
		readonly value: IntakeChannelValue
		readonly label: string
	}>
	readonly onChange: (value: string) => void
	readonly onToggleChannel: (channel: IntakeChannelValue) => void
	readonly channelErrors: readonly string[]
}

export function ConsumerContactSection({
	resources,
	value,
	errors,
	selectedChannels,
	options,
	onToggleChannel,
	channelErrors,
	onChange,
}: Props) {
	const selectedSet = new Set(selectedChannels)

	return (
		<Section
			title="Consumer contact line"
			description="Match the exact text printed on packaging so generated SOPs align with what customers see."
			mocraSections={resources.mocraSections}
			templates={resources.templates}
		>
			<TextareaField
				labelProps={{
					children:
						'What consumer-facing contact line is printed on your labels?',
				}}
				textareaProps={{
					name: 'consumerContactLine',
					value,
					onChange: (event) => onChange(event.target.value),
					rows: 2,
				}}
				descriptionProps={{
					children:
						'E.g. "Lydra AI, Inc., Austin TX 78701, +1-800-555-0123, support@lydra.ai". This must match the packaging to comply with MoCRA (Modernization of Cosmetics Regulation Act).',
				}}
				errors={errors.length > 0 ? [...errors] : undefined}
			/>
			<div className="mt-6 space-y-3">
				<div className="space-y-2">
					<p className="text-sm font-medium">
						Which intake channels are on your product labels?
					</p>
					<p className="text-muted-foreground text-xs">
						Select every channel you publish so SOP outputs mirror the consumer
						instructions on packaging. Labels must list a domestic address,
						phone, or electronic contact for reporting adverse events.
					</p>
				</div>
				{options.length > 0 ? (
					<ul className="space-y-3" role="list">
						{options.map((option) => (
							<IntakeChannelCard
								key={option.value}
								option={option}
								selected={selectedSet.has(option.value)}
								icon={getIntakeChannelIcon(option.value)}
								onToggle={(_nextSelected) => onToggleChannel(option.value)}
								toggleOnPress
							/>
						))}
					</ul>
				) : (
					<p className="text-muted-foreground text-xs">
						Add intake channels under{' '}
						<Link
							to="/mocra/adverse-events"
							className="underline underline-offset-2"
						>
							Adverse Event Ops
						</Link>{' '}
						to make them available here.
					</p>
				)}
				{channelErrors.length > 0 ? (
					<ul className="text-destructive list-disc pl-5 text-xs">
						{channelErrors.map((error) => (
							<li key={error}>{error}</li>
						))}
					</ul>
				) : null}
			</div>
		</Section>
	)
}
